import React, { useState, useEffect, useRef } from 'react';

const EMPTY_FORM = {
  name: '',
  shortDesc: '',
  description: '',
  image: '',
  specs: [],
  applications: [],
};

const ProductForm = ({ onSubmit, initialData, onCancel }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState('');
  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState('');
  const fileRef = useRef(null);
  const tagRef = useRef(null);

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || '',
        shortDesc: initialData.shortDesc || '',
        description: initialData.description || '',
        image: initialData.image || '',
        specs: initialData.specs ? initialData.specs.map((s) => ({ ...s })) : [],
        applications: initialData.applications ? [...initialData.applications] : [],
      });
      setPreview(initialData.image || '');
    } else {
      setForm(EMPTY_FORM);
      setPreview('');
    }
    setTagInput('');
    setErrors({});
  }, [initialData]);

  // ── Image ────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, image: 'Please upload a valid image file.' }));
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPreview(ev.target.result);
      setForm((prev) => ({ ...prev, image: ev.target.result }));
      setErrors((prev) => ({ ...prev, image: '' }));
    };
    reader.readAsDataURL(file);
  };

  // ── Specs ────────────────────────────────────────────────
  const addSpec = () =>
    setForm((prev) => ({ ...prev, specs: [...prev.specs, { label: '', value: '' }] }));

  const updateSpec = (index, field, val) =>
    setForm((prev) => {
      const specs = prev.specs.map((s, i) => (i === index ? { ...s, [field]: val } : s));
      return { ...prev, specs };
    });

  const removeSpec = (index) =>
    setForm((prev) => ({ ...prev, specs: prev.specs.filter((_, i) => i !== index) }));

  // ── Applications / Tags ──────────────────────────────────
  const commitTag = () => {
    const trimmed = tagInput.trim().replace(/,+$/, '');
    if (!trimmed) return;
    if (!form.applications.includes(trimmed)) {
      setForm((prev) => ({ ...prev, applications: [...prev.applications, trimmed] }));
    }
    setTagInput('');
  };

  const handleTagKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitTag();
    }
    if (e.key === 'Backspace' && !tagInput && form.applications.length) {
      setForm((prev) => ({ ...prev, applications: prev.applications.slice(0, -1) }));
    }
  };

  const removeTag = (index) =>
    setForm((prev) => ({ ...prev, applications: prev.applications.filter((_, i) => i !== index) }));

  // ── Validation ───────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Product name is required.';
    if (!form.shortDesc.trim()) e.shortDesc = 'Short description is required.';
    if (!form.description.trim()) e.description = 'Full description is required.';
    if (!form.image) e.image = 'Product image is required.';
    return e;
  };

  // ── Submit ───────────────────────────────────────────────
  const handleSubmit = () => {
    if (tagInput.trim()) commitTag();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    const cleanedSpecs = form.specs.filter((s) => s.label.trim() || s.value.trim());

    onSubmit(
      initialData
        ? { ...initialData, ...form, specs: cleanedSpecs, id: initialData.id }
        : { ...form, specs: cleanedSpecs }
    );

    setForm(EMPTY_FORM);
    setPreview('');
    setTagInput('');
    setErrors({});
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setPreview('');
    setTagInput('');
    setErrors({});
    if (fileRef.current) fileRef.current.value = '';
    onCancel();
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="product-form">

      {/* Image */}
      <div className="pf-field">
        <label>Product Image</label>
        <div className="img-upload-area" onClick={() => fileRef.current?.click()} title="Click to upload">
          {preview
            ? <img src={preview} alt="Product preview" className="img-preview" />
            : <div className="upload-placeholder"><span>📷</span><p>Click to upload image</p></div>}
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
        {errors.image && <span className="field-error">{errors.image}</span>}
      </div>

      {/* Name */}
      <div className="pf-field">
        <label htmlFor="pf-name">Product Name</label>
        <input
          id="pf-name"
          type="text"
          value={form.name}
          placeholder="e.g. Pallet Truck"
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
        />
        {errors.name && <span className="field-error">{errors.name}</span>}
      </div>

      {/* Short Description */}
      <div className="pf-field">
        <label htmlFor="pf-short">
          Short Description <span className="pf-hint">(shown on product card)</span>
        </label>
        <input
          id="pf-short"
          type="text"
          value={form.shortDesc}
          placeholder="e.g. Manual & electric pallet trucks for smooth warehouse operations."
          onChange={(e) => setForm((prev) => ({ ...prev, shortDesc: e.target.value }))}
        />
        {errors.shortDesc && <span className="field-error">{errors.shortDesc}</span>}
      </div>

      {/* Full Description */}
      <div className="pf-field">
        <label htmlFor="pf-desc">
          Full Description <span className="pf-hint">(shown in product modal)</span>
        </label>
        <textarea
          id="pf-desc"
          rows={4}
          value={form.description}
          placeholder="Describe the product in detail..."
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
        />
        {errors.description && <span className="field-error">{errors.description}</span>}
      </div>

      {/* ── Technical Specifications ── */}
      <div className="pf-field">
        <label>
          Technical Specifications <span className="pf-hint">(optional)</span>
        </label>

        {form.specs.length > 0 && (
          <div className="spec-rows">
            {form.specs.map((spec, i) => (
              <div className="spec-row" key={i}>
                <input
                  type="text"
                  className="spec-input"
                  placeholder="Label  e.g. Capacity"
                  value={spec.label}
                  onChange={(e) => updateSpec(i, 'label', e.target.value)}
                />
                <input
                  type="text"
                  className="spec-input"
                  placeholder="Value  e.g. 1,000 – 3,000 kg"
                  value={spec.value}
                  onChange={(e) => updateSpec(i, 'value', e.target.value)}
                />
                <span className="spec-remove" onClick={() => removeSpec(i)} title="Remove row">✕</span>
              </div>
            ))}
          </div>
        )}

        <div className="pf-add-row" onClick={addSpec}>
          ＋ Add Specification Row
        </div>
      </div>

      {/* ── Industries & Applications ── */}
      <div className="pf-field">
        <label>
          Industries &amp; Applications <span className="pf-hint">(optional — press Enter or comma to add)</span>
        </label>

        <div className="tag-input-wrap" onClick={() => tagRef.current?.focus()}>
          {form.applications.map((tag, i) => (
            <span className="app-tag-admin" key={i}>
              {tag}
              <span className="tag-remove" onClick={(e) => { e.stopPropagation(); removeTag(i); }}>✕</span>
            </span>
          ))}
          <input
            ref={tagRef}
            type="text"
            className="tag-input"
            placeholder={form.applications.length ? '' : 'e.g. Warehouses'}
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKey}
            onBlur={commitTag}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="pf-actions">
        <div className="pf-submit" onClick={handleSubmit}>
          {initialData ? '✓  Update Product' : '+  Add Product'}
        </div>
        {initialData && (
          <div className="pf-cancel" onClick={handleCancel}>✕  Cancel</div>
        )}
      </div>

    </div>
  );
};

export default ProductForm;
